import React, {
  Children,
  FC,
  isValidElement,
  PropsWithChildren,
  ReactElement,
  useEffect,
  useState,
} from "react";

interface ConditionalProps {
  condition: boolean;
  unmountOnExit?: boolean;
  onlyAllow?: React.ComponentType;
}

function getUniqueKey(): string {
  return Math.random().toString(36).substring(2, 9);
}

const Conditional: FC<PropsWithChildren<ConditionalProps>> = ({
  condition,
  unmountOnExit = true,
  onlyAllow,
  children,
}) => {
  const [firstRender, setFirstRender] = useState(false);

  useEffect(() => {
    if (!firstRender && condition) {
      setFirstRender(true);
    }
  }, [condition, firstRender]);

  if (onlyAllow) {
    const onlyAllowName = onlyAllow.displayName || onlyAllow.name;

    const isInvalidChild = Children.toArray(children).some((child) => {
      if (!isValidElement(child)) {
        return false;
      }

      const childType = child.type as any;
      const childName = childType.displayName || childType.name;

      return childName !== onlyAllowName;
    });

    if (isInvalidChild) {
      throw new Error(
        `Invalid child element. Only ${onlyAllowName} components are allowed.`
      );
    }
  }

  if (!condition && (unmountOnExit || !firstRender)) {
    return null;
  }

  return (
    <>
      {Children.toArray(children).map((child) => {
        if (isValidElement(child)) {
          return React.cloneElement(child as ReactElement, {
            style: {
              ...child.props.style,
              display: condition ? "" : "none",
            },
            key: child.key || getUniqueKey(),
          });
        }
        return child;
      })}
    </>
  );
};

export default Conditional;
